import React from 'react';
import { Pencil, ChevronRight } from 'lucide-react';
import type { PortfolioSite } from '../../../redux/api/portfolioApi';

interface SitesOverviewTableProps {
  sites: PortfolioSite[];
  onSiteClick: (site: PortfolioSite) => void;
  onEditPortfolio?: () => void;
  onViewAll?: () => void;
}

// Mock health status based on entity count as proxy (real implementation would use events/tasks)
function getSiteHealth(site: PortfolioSite): { color: string; label: string } {
  const entityCount = site.entity_count || 0;
  if (entityCount > 50) return { color: '#EF4444', label: 'critical' };
  if (entityCount > 20) return { color: '#F59E0B', label: 'warning' };
  return { color: '#22C55E', label: 'ok' };
}

const MAX_VISIBLE = 7;

const SitesOverviewTable: React.FC<SitesOverviewTableProps> = ({
  sites,
  onSiteClick,
  onEditPortfolio,
  onViewAll,
}) => {
  const visibleSites = sites.slice(0, MAX_VISIBLE);
  const remaining = sites.length - MAX_VISIBLE;

  return (
    <div>
      <div className="eg-table-container">
        <table className="w-full">
          <thead>
            <tr className="eg-table-header">
              <th className="eg-table-header-cell">Site</th>
              <th className="eg-table-header-cell text-right">Tasks</th>
              <th className="eg-table-header-cell text-right">Safety</th>
              <th className="eg-table-header-cell text-right">Loss (MWh)</th>
              <th className="eg-table-header-cell text-right">Next</th>
            </tr>
          </thead>
          <tbody>
            {visibleSites.map((site, i) => {
              const health = getSiteHealth(site);
              return (
                <tr
                  key={site.site_id || site.id}
                  className={`eg-table-row cursor-pointer ${i % 2 === 1 ? 'eg-table-row-alt' : ''}`}
                  onClick={() => onSiteClick(site)}
                >
                  <td className="eg-table-cell">
                    <div className="flex items-center gap-2 group/site">
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: health.color }}
                      />
                      <span className="font-medium text-[#17c480] group-hover/site:underline">{site.site_name || site.name}</span>
                      <ChevronRight className="w-3.5 h-3.5 text-[#17c480] opacity-0 -translate-x-1 group-hover/site:opacity-100 group-hover/site:translate-x-0 transition-all" />
                    </div>
                  </td>
                  <td className="eg-table-cell text-right" style={{ color: 'var(--color-text-secondary)' }}>
                    {/* Mock task count */}
                    {Math.floor(Math.random() * 10) + 1}
                  </td>
                  <td className="eg-table-cell text-right" style={{ color: 'var(--color-text-secondary)' }}>
                    {health.label === 'critical' ? '2' : health.label === 'warning' ? '1' : '0'}
                  </td>
                  <td className="eg-table-cell text-right" style={{ color: 'var(--color-text-secondary)' }}>
                    {/* Mock power loss */}
                    {(Math.random() * 3).toFixed(1)}
                  </td>
                  <td className="eg-table-cell text-right" style={{ color: 'var(--color-text-muted)' }}>
                    {/* Mock next milestone */}
                    {Math.floor(Math.random() * 90) + 1}d
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-3 px-1">
        <div className="flex items-center gap-3">
          {remaining > 0 && (
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              ...{remaining} more
            </span>
          )}
          {onViewAll && (
            <button
              onClick={onViewAll}
              className="text-xs font-medium text-[#17c480] hover:text-[#17c480]/80 transition-colors"
            >
              All &rarr;
            </button>
          )}
        </div>
        {onEditPortfolio && (
          <button
            onClick={onEditPortfolio}
            className="flex items-center gap-1 text-xs transition-colors"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <Pencil className="w-3 h-3" />
            Edit Portfolio
          </button>
        )}
      </div>
    </div>
  );
};

export default SitesOverviewTable;
